import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CountryDto } from '@app/generated/models/country-dto';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountryCode, isSupportedCountry, parsePhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import { CountryService } from '@app/shared/services/country.service';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Inject } from '@angular/core';

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.css'],
  standalone: false,
})
export class PhoneNumberInputComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() selectedPhoneNumber = new EventEmitter<string>();
  @Input() showLabel = true;

  @ViewChild('dropdown') dropdown: ElementRef | undefined;
  @ViewChild('toggleButton') toggleButton: ElementRef | undefined;
  @ViewChild('phoneInput') phoneInput: ElementRef<HTMLInputElement> | undefined;

  showDropdown = false;
  selectedCountry?: CountryDto;
  countries: CountryDto[] = [];
  filteredCountries: CountryDto[] = [];
  userEnteredNumber = '';
  validationMessage = '';
  validationMessageType: 'error' | 'info' | 'none' = 'none';
  private unsubscribe$ = new Subject<void>();
  private cleanupInputWatcher?: () => void;
  private isInternalUpdate = false; // Flag to prevent recursive calls

  /* eslint-disable */
  private dictErrorToMessage = {
    NOT_A_NUMBER: 'phone-number-message-only-numbers',
    INVALID_COUNTRY: 'phone-number-message-select-country',
    TOO_SHORT: 'phone-number-message-too-short',
    INVALID_LENGTH: 'phone-number-message-too-short',
    TOO_LONG: 'phone-number-message-too-long',
  };
  /* eslint-enable */
  private _value = '';

  constructor(
    private countryService: CountryService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  get dropdownHeight(): string {
    const rowHeight = 42;
    const numberOfRows = Math.min(3, this.filteredCountries.length);

    return `${numberOfRows * rowHeight}px`;
  }

  get value(): string {
    return this._value;
  }

  @Input()
  set value(value: string) {
    if (!value) {
      this._value = '';
      return;
    }

    // Clean up the value first
    const cleanValue = value.replace(/\s+/g, '');

    if (cleanValue.startsWith('+')) {
      value = cleanValue;
    } else {
      value = '+' + cleanValue;
    }

    if (!/^\+?\d+$/.test(value)) {
      this._value = '';
      return;
    }

    this._value = value;
    this.processPendingPhoneNumber();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (
      this.showDropdown &&
      !this.dropdown?.nativeElement.contains(event.target) &&
      !this.toggleButton?.nativeElement.contains(event.target)
    ) {
      this.showDropdown = false;
    }
  }

  ngOnInit(): void {
    this.countryService
      .getCountries()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (countries: CountryDto[]) => {
          this.countries = countries;
          this.filteredCountries = this.countries;
          this.selectCountry(this.countries.find((country) => country.code === 'CZ') || this.countries[0]);
          this.processPendingPhoneNumber();
        },
      });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.phoneInput?.nativeElement) {
      this.setupInputWatcher();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    // Clean up input watcher
    if (this.cleanupInputWatcher) {
      this.cleanupInputWatcher();
    }
  }

  /**
   * Sets up comprehensive input value watching that detects both user input
   * and programmatic changes (like console assignments)
   */
  private setupInputWatcher(): void {
    const input = this.phoneInput!.nativeElement;

    // Inspiration: https://stackoverflow.com/questions/55033836/how-do-you-listen-detect-changes-to-an-input-value-when-the-input-value-is-c
    // Override the value property to catch direct assignments
    const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    Object.defineProperty(input, 'value', {
      configurable: true,
      get: originalDescriptor!.get,
      set: (newValue: string) => {
        // Use the original setter first
        originalDescriptor!.set!.call(input, newValue);

        // Only process if it's not an internal update and the value actually changed
        if (!this.isInternalUpdate && newValue !== this.userEnteredNumber) {
          // Use setTimeout to avoid infinite loops and ensure DOM is updated
          setTimeout(() => {
            this.parseAndSetFormattedPhoneNumber(newValue);
          }, 0);
        }
      },
    });

    // Store cleanup function
    this.cleanupInputWatcher = () => {
      if (originalDescriptor) {
        Object.defineProperty(input, 'value', originalDescriptor);
      }
    };
  }

  onPhoneNumberChange(): void {
    this.validatePhoneNumber();
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  getFlagUrl(countryCode: string | undefined): string {
    if (!countryCode) {
      return '/assets/phone-flags/placeholder.svg';
    }
    return `/assets/phone-flags/${countryCode?.toLowerCase()}.svg`;
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.showDropdown = false;
    this.filteredCountries = [...this.countries];
    this.reorderCountries();
    if (this.userEnteredNumber.length > 0) {
      this.validatePhoneNumber();
    }
  }

  onSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    const lowerCaseSearchTerm = this.removeDiacritics(searchTerm.toLowerCase());

    this.filteredCountries = this.countries.filter((country) => {
      const matchDialingCode = country.dialingCode.includes(searchTerm);
      const matchName = this.removeDiacritics(country.name.toLowerCase()).includes(lowerCaseSearchTerm);

      return matchDialingCode || matchName;
    });

    this.reorderCountries();
  }

  onInputBlur(): void {
    this.validatePhoneNumber();
  }

  onFormattedPhonePasted(formattedPhone: string): void {
    // Process the pasted formatted phone number
    this.parseAndSetFormattedPhoneNumber(formattedPhone);
  }

  parseAndSetFormattedPhoneNumber(formattedPhone: string): void {
    if (!formattedPhone) return;

    // Remove all non-digit characters except for the leading '+'
    const normalizedPhone = formattedPhone.trim();
    let digitsOnly = '';

    if (normalizedPhone.startsWith('+')) {
      digitsOnly = '+' + normalizedPhone.substring(1).replace(/\D/g, '');
    } else {
      digitsOnly = normalizedPhone.replace(/\D/g, '');
      if (digitsOnly) {
        digitsOnly = '+' + digitsOnly;
      }
    }

    if (digitsOnly && this.countries.length > 0) {
      // Find the country that matches this dialing code
      const matchedCountry = this.countries.find((country) => digitsOnly.startsWith(country.dialingCode));

      if (matchedCountry) {
        // Set the selected country
        this.selectedCountry = matchedCountry;
        this.reorderCountries();

        // Set the user entered number (without country code)
        const phoneWithoutCountryCode = digitsOnly.replace(matchedCountry.dialingCode, '');
        this.userEnteredNumber = phoneWithoutCountryCode;

        // Set the complete value
        this._value = digitsOnly;

        // Update the input field with just the phone number (without country code)
        // Use the flag to prevent recursive calls
        this.isInternalUpdate = true;
        if (this.phoneInput?.nativeElement) {
          this.phoneInput.nativeElement.value = phoneWithoutCountryCode;
        }
        this.isInternalUpdate = false;

        // Validate the phone number
        this.validatePhoneNumber();
      }
    }
  }

  private validatePhoneNumber(): void {
    if (!this.selectedCountry || !this.selectedCountry.code) {
      return;
    }

    const countryCode = this.selectedCountry?.code as CountryCode;

    this.validationMessageType = 'none';
    this.selectedPhoneNumber.emit(undefined);

    if (!isSupportedCountry(countryCode)) {
      this.selectedPhoneNumber.emit(this.selectedCountry.dialingCode + this.userEnteredNumber);
      return;
    }

    if (this.userEnteredNumber.length === 0) {
      this.setValidationMessage('error', 'phone-number-message-empty');
      return;
    }
    const validationResult = validatePhoneNumberLength(this.userEnteredNumber, countryCode);
    if (validationResult) {
      const errorMessage = this.dictErrorToMessage[validationResult] ?? 'error.unknown';
      this.setValidationMessage('error', errorMessage);
      return;
    }

    this.selectedPhoneNumber.emit(this.selectedCountry.dialingCode + this.userEnteredNumber);
    const parsedNumber = parsePhoneNumber(this.userEnteredNumber, countryCode);
    if (parsedNumber.isValid()) {
      this.setValidationMessage('none', '');
    } else {
      this.setValidationMessage('info', 'phone-number-message');
    }
  }

  private setValidationMessage(type: 'none' | 'info' | 'error', messageKey: string): void {
    this.validationMessageType = type;
    this.validationMessage = messageKey ? this.translate.instant(messageKey) : '';
  }

  private removeDiacritics(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private reorderCountries(): void {
    if (!this.selectedCountry) {
      return;
    }

    if (!this.filteredCountries.includes(this.selectedCountry)) {
      this.filteredCountries.unshift(this.selectedCountry);
    } else {
      const index = this.filteredCountries.indexOf(this.selectedCountry);
      if (index > 0) {
        this.filteredCountries.splice(index, 1);
        this.filteredCountries.unshift(this.selectedCountry);
      }
    }
  }

  private processPendingPhoneNumber(): void {
    if (this._value && this.countries.length > 0) {
      const matchedCountry = this.countries.find((country) => this._value?.startsWith(country.dialingCode));
      if (matchedCountry) {
        this.selectedCountry = matchedCountry;
        this.reorderCountries();
        this.userEnteredNumber = this._value.replace(matchedCountry.dialingCode, '');
      } else {
        this._value = '';
      }
    }
  }
}
